// src/pages/Catalog/ServiceSectionsPage.jsx
//
// Service Sections page
// =====================
//
// PURPOSE
// -------
// Manage second-level service groupings that belong to a service main category.
//
// This page adds one small but important UX element compared to ServiceMainCategoriesPage:
// - a category filter above the table
//
// Why?
// Because sections become much easier to browse when staff can narrow the list
// to one main category at a time.
//

import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import "./ServiceSectionsPage.css";

import {
  fetchServiceSections,
  createServiceSection,
  updateServiceSection,
  toggleServiceSectionActive,
  deleteServiceSection,
} from "../../services/serviceSectionsService";
import { fetchServiceMainCategories } from "../../services/serviceMainCategoriesService";

function asBool(v) {
  return v === true || String(v).trim().toLowerCase() === "true" || String(v).trim() === "1";
}

function toNumberOrZero(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function slugify(input) {
  return String(input || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 150);
}

function initialForm() {
  return {
    main_category_id: "",
    name_internal: "",
    name_public: "",
    slug: "",
    intro_text: "",
    notes_public: "",
    sort_order: 0,
    is_active: true,
    publish_to_website: true,
  };
}

export default function ServiceSectionsPage() {
  const { t } = useTranslation("catalog");

  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [selectedMainCategoryId, setSelectedMainCategoryId] = useState("");

  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState("CREATE");
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(initialForm());

  const activeCount = useMemo(() => rows.filter((r) => asBool(r.is_active)).length, [rows]);

  async function loadCategories() {
    const data = await fetchServiceMainCategories({ active: true });
    setCategories(Array.isArray(data) ? data : []);
  }

  async function loadRows() {
    setLoading(true);
    setError("");

    try {
      const params = selectedMainCategoryId
        ? { main_category_id: selectedMainCategoryId }
        : {};

      const data = await fetchServiceSections(params);
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.message || t("serviceSections.messages.loadFailed"));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    (async () => {
      try {
        await loadCategories();
        await loadRows();
      } catch (e) {
        setError(e?.message || t("serviceSections.messages.initializeFailed"));
      }
    })();
  }, []);

  useEffect(() => {
    loadRows();
  }, [selectedMainCategoryId]);

  function openCreate() {
    setEditorMode("CREATE");
    setEditingId(null);
    setForm((prev) => ({
      ...initialForm(),
      main_category_id: selectedMainCategoryId || "",
    }));
    setEditorOpen(true);
    setError("");
    setSuccess("");
  }

  function openEdit(row) {
    setEditorMode("EDIT");
    setEditingId(Number(row.id));
    setForm({
      main_category_id: row.main_category_id ? String(row.main_category_id) : "",
      name_internal: row.name_internal || "",
      name_public: row.name_public || "",
      slug: row.slug || "",
      intro_text: row.intro_text || "",
      notes_public: row.notes_public || "",
      sort_order: Number(row.sort_order || 0),
      is_active: asBool(row.is_active),
      publish_to_website: asBool(row.publish_to_website),
    });
    setEditorOpen(true);
    setError("");
    setSuccess("");
  }

  function closeEditor() {
    setEditorOpen(false);
    setEditingId(null);
    setForm(initialForm());
  }

  function handleNamePublicBlurAutoSlug() {
    setForm((prev) => {
      if ((prev.slug || "").trim() !== "") return prev;
      return { ...prev, slug: slugify(prev.name_public) };
    });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setError("");
    setSuccess("");

    try {
      const payload = {
        ...form,
        main_category_id: Number(form.main_category_id),
        sort_order: toNumberOrZero(form.sort_order),
      };

      if (editorMode === "CREATE") {
        await createServiceSection(payload);
        setSuccess(t("serviceSections.messages.created"));
      } else {
        await updateServiceSection(editingId, payload);
        setSuccess(t("serviceSections.messages.updated"));
      }

      closeEditor();
      await loadRows();
    } catch (e2) {
      setError(e2?.message || t("serviceSections.messages.saveFailed"));
    } finally {
      setSaving(false);
    }
  }

  async function handleToggleActive(row) {
    setError("");
    setSuccess("");

    try {
      await toggleServiceSectionActive(row.id, !asBool(row.is_active));
      setSuccess(
        t("serviceSections.messages.statusChanged", {
          status: asBool(row.is_active)
            ? t("serviceSections.status.deactivated")
            : t("serviceSections.status.activated"),
        })
      );
      await loadRows();
    } catch (e) {
      setError(e?.message || t("serviceSections.messages.statusChangeFailed"));
    }
  }

  async function handleDelete(row) {
    const confirmed = window.confirm(
      t("serviceSections.confirmDelete", { name: row.name_public })
    );

    if (!confirmed) return;

    setError("");
    setSuccess("");

    try {
      await deleteServiceSection(row.id);

      if (editorOpen && Number(editingId) === Number(row.id)) {
        closeEditor();
      }

      setSuccess(t("serviceSections.messages.deleted"));
      await loadRows();
    } catch (e) {
      setError(e?.message || t("serviceSections.messages.deleteFailed"));
    }
  }  

  return (
    <div className="ssc-page">
      <div className="ssc-page__inner">
        <div className="ssc-page__header">
          <div>
            <h1>{t("serviceSections.title")}</h1>
            <p>
              {t("serviceSections.subtitle")}
            </p>
          </div>

          <div className="ssc-page__headerActions">
            <button type="button" className="ssc-btn ssc-btn--primary" onClick={openCreate}>
              {t("serviceSections.actions.add")}
            </button>
          </div>
        </div>

        <div className="ssc-page__card">
          <div className="ssc-toolbar">
            <div className="ssc-toolbar__filters">
              <div className="ssc-field ssc-field--compact">
                <label>{t("serviceSections.filters.mainCategory")}</label>
                <select
                  value={selectedMainCategoryId}
                  onChange={(e) => setSelectedMainCategoryId(e.target.value)}
                >
                  <option value="">{t("serviceSections.filters.allCategories")}</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={String(cat.id)}>
                      {cat.name_public}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="ssc-page__meta">
              <span>{t("serviceSections.meta.total", { count: rows.length })}</span>
              <span>{t("serviceSections.meta.active", { count: activeCount })}</span>
            </div>
          </div>

          {error ? <div className="ssc-alert ssc-alert--error">{error}</div> : null}
          {success ? <div className="ssc-alert ssc-alert--success">{success}</div> : null}

          {editorOpen && (
            <form className="ssc-editor" onSubmit={handleSubmit}>
              <div className="ssc-editor__grid">
                <div className="ssc-field">
                  <label>{t("serviceSections.fields.mainCategory")}</label>
                  <select
                    value={form.main_category_id}
                    onChange={(e) => setForm((prev) => ({ ...prev, main_category_id: e.target.value }))}
                    required
                  >
                    <option value="">{t("serviceSections.placeholders.selectCategory")}</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={String(cat.id)}>
                        {cat.name_public}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="ssc-field">
                  <label>{t("serviceSections.fields.sortOrder")}</label>
                  <input
                    type="number"
                    value={form.sort_order}
                    onChange={(e) => setForm((prev) => ({ ...prev, sort_order: e.target.value }))}
                  />
                </div>

                <div className="ssc-field">
                  <label>{t("serviceSections.fields.internalName")}</label>
                  <input
                    value={form.name_internal}
                    onChange={(e) => setForm((prev) => ({ ...prev, name_internal: e.target.value }))}
                    required
                  />
                </div>

                <div className="ssc-field">
                  <label>{t("serviceSections.fields.publicName")}</label>
                  <input
                    value={form.name_public}
                    onChange={(e) => setForm((prev) => ({ ...prev, name_public: e.target.value }))}
                    onBlur={handleNamePublicBlurAutoSlug}
                    required
                  />
                </div>

                <div className="ssc-field ssc-field--full">
                  <label>{t("serviceSections.fields.slug")}</label>
                  <input
                    value={form.slug}
                    onChange={(e) => setForm((prev) => ({ ...prev, slug: e.target.value }))}
                    placeholder={t("serviceSections.placeholders.autoSlug")}
                  />
                </div>

                <div className="ssc-field ssc-field--full">
                  <label>{t("serviceSections.fields.introText")}</label>
                  <textarea
                    rows="4"
                    value={form.intro_text}
                    onChange={(e) => setForm((prev) => ({ ...prev, intro_text: e.target.value }))}
                  />
                </div>

                <div className="ssc-field ssc-field--full">
                  <label>{t("serviceSections.fields.publicNotes")}</label>
                  <textarea
                    rows="3"
                    value={form.notes_public}
                    onChange={(e) => setForm((prev) => ({ ...prev, notes_public: e.target.value }))}
                  />
                </div>
              </div>

              <div className="ssc-editor__checks">
                <label>
                  <input
                    type="checkbox"
                    checked={!!form.is_active}
                    onChange={(e) => setForm((prev) => ({ ...prev, is_active: e.target.checked }))}
                  />
                  <span>{t("serviceSections.fields.active")}</span>
                </label>

                <label>
                  <input
                    type="checkbox"
                    checked={!!form.publish_to_website}
                    onChange={(e) => setForm((prev) => ({ ...prev, publish_to_website: e.target.checked }))}
                  />
                  <span>{t("serviceSections.fields.publishToWebsite")}</span>
                </label>
              </div>

              <div className="ssc-editor__actions">
                <button type="submit" className="ssc-btn ssc-btn--primary" disabled={saving}>
                  {saving
                    ? t("serviceSections.actions.saving")
                    : editorMode === "CREATE"
                      ? t("serviceSections.actions.create")
                      : t("serviceSections.actions.saveChanges")}
                </button>
                <button type="button" className="ssc-btn" onClick={closeEditor} disabled={saving}>
                  {t("serviceSections.actions.cancel")}
                </button>
              </div>
            </form>
          )}

          <div className="ssc-tableArea">
            <div className="ssc-tableWrap">
              <table className="ssc-table">
                <thead>
                  <tr>
                    <th className="ssc-left">{t("serviceSections.columns.mainCategory")}</th>
                    <th className="ssc-left">{t("serviceSections.columns.publicName")}</th>
                    <th className="ssc-left">{t("serviceSections.columns.slug")}</th>
                    <th className="ssc-left">{t("serviceSections.columns.sort")}</th>
                    <th className="ssc-left">{t("serviceSections.columns.active")}</th>
                    <th className="ssc-left">{t("serviceSections.columns.publish")}</th>
                    <th className="ssc-right">{t("serviceSections.columns.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {!loading && rows.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="ssc-empty">{t("serviceSections.empty")}</td>
                    </tr>
                  ) : null}

                  {rows.map((row) => (
                    <tr key={row.id} className={!asBool(row.is_active) ? "ssc-rowMuted" : ""}>
                      <td className="ssc-left">{row.mainCategory?.name_public || "—"}</td>
                      <td className="ssc-left">{row.name_public}</td>
                      <td className="ssc-left"><code>{row.slug}</code></td>
                      <td className="ssc-left">{row.sort_order}</td>
                      <td className="ssc-left">{asBool(row.is_active) ? t("serviceSections.boolean.yes") : t("serviceSections.boolean.no")}</td>
                      <td className="ssc-left">{asBool(row.publish_to_website) ? t("serviceSections.boolean.yes") : t("serviceSections.boolean.no")}</td>
                      <td className="ssc-right">
                        <div className="ssc-actions">
                          <button type="button" className="ssc-btn ssc-btn--small" onClick={() => openEdit(row)}>
                            {t("serviceSections.actions.edit")}
                          </button>
                          <button
                            type="button"
                            className="ssc-btn ssc-btn--small"
                            onClick={() => handleToggleActive(row)}
                          >
                            {asBool(row.is_active)
                              ? t("serviceSections.actions.deactivate")
                              : t("serviceSections.actions.activate")}
                          </button>
                          <button
                            type="button"
                            className="ssc-btn ssc-btn--small ssc-btn--danger"
                            onClick={() => handleDelete(row)}
                            title={t("serviceSections.actions.deleteTitle")}
                          >
                            {t("serviceSections.actions.delete")}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


